import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import {
  ActivityItem,
  Application,
  AttentionItem,
  Band,
  CohortBandItem,
  CollegePage,
  DashboardSettings,
  DepartmentPlacementRate,
  InsightGroup,
  Job,
  KpiOverview,
  NotificationItem,
  Priority,
  ReadinessKpis,
  Student
} from '../models/college-dashboard.model';

const STORAGE_KEY = 'placement_dashboard_settings';

@Injectable({
  providedIn: 'root'
})
export class CollegeDashboardService {
  private readonly defaultSettings: DashboardSettings = {
    academicYear: 'All Years',
    department: 'All Departments',
    weeklyEmails: true,
    opportunityAlerts: true,
    practiceCredits: false,
    institutionName: 'P.K. College of Engineering',
    placementOfficer: 'Placement Officer',
    contactEmail: 'placements@pkce.edu'
  };

  private studentsData: Student[] = [
    {
      id: 'STU-2401',
      name: 'Aarav Mehta',
      email: 'aarav.mehta@campus.edu',
      department: 'CSE',
      year: 'Final Year',
      score: 88,
      creditsUsed: 12,
      placementStatus: 'In Process',
      applications: 8,
      activeApplications: 3,
      assessmentsTaken: 4,
      assessmentsCleared: 3,
      interviewsTaken: 2,
      interviewsCleared: 1,
      assessmentsList: [
        { name: 'Python Fundamentals (TCS)', cleared: true },
        { name: 'Logical Reasoning (Infosys)', cleared: true },
        { name: 'Frontend Challenge (Wipro)', cleared: false },
        { name: 'Communication Skills', cleared: true }
      ],
      interviewsList: [
        { name: 'TCS', detail: 'Technical Round 1', cleared: true },
        { name: 'Infosys', detail: 'HR Round', cleared: false }
      ],
      profileComplete: true,
      practiceAssessmentCredits: 8,
      practiceInterviewCredits: 4
    },
    {
      id: 'STU-2388',
      name: 'Diya Sharma',
      email: 'diya.sharma@campus.edu',
      department: 'ECE',
      year: 'Final Year',
      score: 82,
      creditsUsed: 8,
      placementStatus: 'In Process',
      applications: 6,
      activeApplications: 2,
      assessmentsTaken: 2,
      assessmentsCleared: 2,
      interviewsTaken: 1,
      interviewsCleared: 1,
      assessmentsList: [
        { name: 'Core Electronics', cleared: true },
        { name: 'Aptitude Test', cleared: true }
      ],
      interviewsList: [
        { name: 'Qualcomm', detail: 'Technical Round 1', cleared: true }
      ],
      profileComplete: false,
      missingFields: ['Resume Upload', 'GitHub Link'],
      practiceAssessmentCredits: 5,
      practiceInterviewCredits: 3
    },
    {
      id: 'STU-2377',
      name: 'Rohan Kapoor',
      email: 'rohan.kapoor@campus.edu',
      department: 'IT',
      year: 'Final Year',
      score: 76,
      creditsUsed: 15,
      placementStatus: 'Unplaced',
      applications: 5,
      activeApplications: 4,
      assessmentsTaken: 8,
      assessmentsCleared: 5,
      interviewsTaken: 2,
      interviewsCleared: 0,
      profileComplete: true,
      practiceAssessmentCredits: 10,
      practiceInterviewCredits: 5
    },
    {
      id: 'STU-2364',
      name: 'Ananya Iyer',
      email: 'ananya.iyer@campus.edu',
      department: 'CSE',
      year: 'Final Year',
      score: 91,
      creditsUsed: 10,
      placementStatus: 'Placed',
      applications: 9,
      activeApplications: 0,
      assessmentsTaken: 14,
      assessmentsCleared: 12,
      interviewsTaken: 6,
      interviewsCleared: 5,
      profileComplete: true,
      practiceAssessmentCredits: 6,
      practiceInterviewCredits: 4
    },
    {
      id: 'STU-2359',
      name: 'Kabir Singh',
      email: 'kabir.singh@campus.edu',
      department: 'Mechanical',
      year: 'Final Year',
      score: 68,
      creditsUsed: 18,
      placementStatus: 'Unplaced',
      applications: 2,
      activeApplications: 1,
      assessmentsTaken: 5,
      assessmentsCleared: 2,
      interviewsTaken: 1,
      interviewsCleared: 0,
      profileComplete: false,
      missingFields: ['10th Marksheet', '12th Marksheet'],
      practiceAssessmentCredits: 12,
      practiceInterviewCredits: 6
    },
    {
      id: 'STU-2341',
      name: 'Meera Nair',
      email: 'meera.nair@campus.edu',
      department: 'EEE',
      year: 'Final Year',
      score: 73,
      creditsUsed: 14,
      placementStatus: 'In Process',
      applications: 4,
      activeApplications: 2,
      assessmentsTaken: 7,
      assessmentsCleared: 4,
      interviewsTaken: 3,
      interviewsCleared: 1,
      profileComplete: true,
      practiceAssessmentCredits: 9,
      practiceInterviewCredits: 5
    },
    {
      id: 'STU-2330',
      name: 'Arjun Rao',
      email: 'arjun.rao@campus.edu',
      department: 'Civil',
      year: 'Final Year',
      score: 64,
      creditsUsed: 4,
      placementStatus: 'Not Eligible',
      applications: 0,
      activeApplications: 0,
      assessmentsTaken: 3,
      assessmentsCleared: 0,
      interviewsTaken: 0,
      interviewsCleared: 0,
      profileComplete: false,
      missingFields: ['Portfolio Link'],
      practiceAssessmentCredits: 2,
      practiceInterviewCredits: 2
    },
    {
      id: 'STU-2318',
      name: 'Ishita Bose',
      email: 'ishita.bose@campus.edu',
      department: 'MBA',
      year: 'Final Year',
      score: 79,
      creditsUsed: 9,
      placementStatus: 'In Process',
      applications: 7,
      activeApplications: 3,
      assessmentsTaken: 9,
      assessmentsCleared: 7,
      interviewsTaken: 4,
      interviewsCleared: 2,
      profileComplete: true,
      practiceAssessmentCredits: 6,
      practiceInterviewCredits: 3
    },
    {
      id: 'STU-2305',
      name: 'Vikram Reddy',
      email: 'vikram.reddy@campus.edu',
      department: 'CSE',
      year: 'Final Year',
      score: 58,
      creditsUsed: 22,
      placementStatus: 'Opted Out',
      applications: 1,
      activeApplications: 0,
      assessmentsTaken: 4,
      assessmentsCleared: 1,
      interviewsTaken: 0,
      interviewsCleared: 0,
      profileComplete: true,
      practiceAssessmentCredits: 14,
      practiceInterviewCredits: 8
    },
    {
      id: 'STU-2291',
      name: 'Sara Khan',
      email: 'sara.khan@campus.edu',
      department: 'IT',
      year: 'Final Year',
      score: 85,
      creditsUsed: 11,
      placementStatus: 'Placed',
      applications: 6,
      activeApplications: 0,
      assessmentsTaken: 11,
      assessmentsCleared: 9,
      interviewsTaken: 5,
      interviewsCleared: 4,
      profileComplete: true,
      practiceAssessmentCredits: 7,
      practiceInterviewCredits: 4
    }
  ];

  private jobsData: Job[] = [
    {
      id: 'JOB-108',
      company: 'TCS',
      role: 'Digital Engineer',
      department: 'All departments',
      location: 'Bengaluru',
      applicants: 231,
      status: 'Applications Open',
      deadline: 'Sep 18, 2025',
      logo: 'T',
      postedDate: '2026-08-08',
      package: '₹7.0 LPA',
      description: 'Development and maintenance of digital applications.'
    },
    {
      id: 'JOB-106',
      company: 'Infosys',
      role: 'Systems Engineer',
      department: 'CSE, IT, ECE',
      location: 'Pune',
      applicants: 184,
      status: 'Assessment Ongoing',
      deadline: 'Sep 12, 2025',
      logo: 'I',
      postedDate: '2026-07-28',
      package: '₹3.6 - ₹5.0 LPA',
      description: 'System integration and software engineering.'
    },
    {
      id: 'JOB-104',
      company: 'Wipro',
      role: 'Associate Engineer',
      department: 'CSE, EEE',
      location: 'Hyderabad',
      applicants: 142,
      status: 'Interviews Ongoing',
      deadline: 'Sep 04, 2025',
      logo: 'W',
      postedDate: '2026-08-09',
      package: '₹4.0 LPA',
      description: 'Assisting in software development lifecycle.'
    },
    {
      id: 'JOB-101',
      company: 'HCLTech',
      role: 'Graduate Engineer',
      department: 'All departments',
      location: 'Noida',
      applicants: 98,
      status: 'Applications Open',
      deadline: 'Sep 22, 2025',
      logo: 'H',
      postedDate: '2026-08-10',
      package: '₹4.25 LPA',
      description: 'Entry level software engineering position.'
    },
    {
      id: 'JOB-099',
      company: 'Zoho',
      role: 'Product Engineer',
      department: 'CSE, IT',
      location: 'Chennai',
      applicants: 76,
      status: 'Assessment Ongoing',
      deadline: 'Aug 29, 2025',
      logo: 'Z',
      postedDate: '2026-08-05',
      package: '₹8.0 - ₹12.0 LPA',
      description: 'Full stack product development.'
    }
  ];

  private applicationsData: Application[] = [
    { studentId: 'STU-2401', jobId: 'JOB-108', status: 'Applied' },
    { studentId: 'STU-2401', jobId: 'JOB-106', status: 'Applied' },
    { studentId: 'STU-2388', jobId: 'JOB-108', status: 'Applied' },
    { studentId: 'STU-2364', jobId: 'JOB-106', status: 'Selected', offerStatus: 'Pending' },
    { studentId: 'STU-2291', jobId: 'JOB-104', status: 'Selected', offerStatus: 'Accepted' },
    { studentId: 'STU-2341', jobId: 'JOB-101', status: 'Selected', offerStatus: 'Pending' }
  ];

  private notificationsData: NotificationItem[] = [
    {
      id: 1,
      title: 'New job posted by Deloitte',
      description: '3 new roles match your campus criteria',
      time: '10 min ago',
      iconType: 'building',
      unread: true
    },
    {
      id: 2,
      title: '73 assessment submissions pending',
      description: 'Python Fundamentals needs follow-up',
      time: '2 hours ago',
      iconType: 'clipboard',
      unread: true
    },
    {
      id: 3,
      title: 'Interview panel conflict detected',
      description: 'TCS · Digital Engineer · Sep 04',
      time: '5 hours ago',
      iconType: 'calendar',
      unread: true
    },
    {
      id: 4,
      title: 'Monthly placement report is ready',
      description: 'Your August review can be downloaded',
      time: 'Yesterday',
      iconType: 'report',
      unread: true
    }
  ];

  private departmentsList: DepartmentPlacementRate[] = [
    { name: 'CSE', value: 82 },
    { name: 'ECE', value: 68 },
    { name: 'IT', value: 61 },
    { name: 'Mech', value: 54 },
    { name: 'EEE', value: 47 },
    { name: 'Civil', value: 39 },
    { name: 'MBA', value: 52 },
    { name: 'MCA', value: 58 }
  ];

  private recentActivities: ActivityItem[] = [
    { text: 'Accenture registered for campus drive', time: '2 hours ago', typeIndex: 0 },
    { text: 'Python assessment completed by 56 students', time: '3 hours ago', typeIndex: 1 },
    { text: 'TCS interview scheduled for 18 students', time: '4 hours ago', typeIndex: 2 },
    { text: '24 students updated their profiles', time: 'Yesterday', typeIndex: 3 },
    { text: 'Deloitte posted 3 new job openings', time: 'Yesterday', typeIndex: 4 }
  ];

  // Observables & Subjects
  private settingsSubject = new BehaviorSubject<DashboardSettings>(this.loadInitialSettings());
  public settings$ = this.settingsSubject.asObservable();

  private notificationsSubject = new BehaviorSubject<NotificationItem[]>(this.notificationsData);
  public notifications$ = this.notificationsSubject.asObservable();

  private toastSubject = new Subject<string>();
  public toast$ = this.toastSubject.asObservable();

  private activePageSubject = new BehaviorSubject<CollegePage>('Overview');
  public activePage$ = this.activePageSubject.asObservable();

  // Selected entities for modals
  public selectedStudent$ = new BehaviorSubject<Student | null>(null);
  public selectedJob$ = new BehaviorSubject<Job | null>(null);
  public isProfileModalOpen$ = new BehaviorSubject<boolean>(false);
  public isAttentionModalOpen$ = new BehaviorSubject<boolean>(false);
  public activeAttentionCategory$ = new BehaviorSubject<'Profiles Incomplete' | 'Offers Awaiting Response' | 'New Companies This Week'>('Profiles Incomplete');

  private loadInitialSettings(): DashboardSettings {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const merged = { ...this.defaultSettings, ...JSON.parse(saved) };
        // Migrate a pre-existing academicYear value from the old
        // "2025–26"-style academic-year-range format (no longer understood
        // by parseYearOfPassing, which now expects a plain 4-digit year or
        // "All Years") back to a safe default, instead of carrying forward
        // a value that silently fails to filter anything and doesn't match
        // any option in the header/Settings dropdowns.
        if (merged.academicYear !== 'All Years' && !/^\d{4}$/.test(merged.academicYear)) {
          merged.academicYear = 'All Years';
        }
        return merged;
      }
    } catch {
      // ignore
    }
    return { ...this.defaultSettings };
  }

  public getSettings(): DashboardSettings {
    return this.settingsSubject.value;
  }

  public saveSettings(updated: Partial<DashboardSettings>, options: { silent?: boolean } = {}): void {
    const current = this.settingsSubject.value;
    const next = { ...current, ...updated };
    this.settingsSubject.next(next);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      // ignore
    }
    if (!options.silent) {
      this.notify('Changes saved');
    }
  }

  public setAcademicYear(year: string): void {
    // Silent: this just changes the active filter, not a "save" the user did.
    this.saveSettings({ academicYear: year }, { silent: true });
  }

  public setDepartment(dept: string): void {
    this.saveSettings({ department: dept }, { silent: true });
  }

  public setActivePage(page: CollegePage): void {
    this.activePageSubject.next(page);
    this.selectedStudent$.next(null);
    this.selectedJob$.next(null);
  }

  public notify(message: string): void {
    this.toastSubject.next(message);
  }

  // Students Data Access
  public getStudents(): Student[] {
    return [...this.studentsData];
  }

  public getFilteredStudents(dept: string, filter: string, query: string): Student[] {
    return this.studentsData.filter((student) => {
      const matchDept = dept === 'All Departments' || student.department === dept;
      const matchFilter = filter === 'All Students' || student.placementStatus === filter;
      const matchQuery =
        !query ||
        `${student.name} ${student.email} ${student.id} ${student.department}`
          .toLowerCase()
          .includes(query.toLowerCase());
      return matchDept && matchFilter && matchQuery;
    });
  }

  // Jobs Data Access
  public getJobs(): Job[] {
    return [...this.jobsData];
  }

  // Applications
  public getApplicationsForStudent(studentId: string): Application[] {
    return this.applicationsData.filter((app) => app.studentId === studentId);
  }

  // Attention Items
  public getIncompleteProfiles(): Student[] {
    return this.studentsData.filter((s) => s.profileComplete === false);
  }

  public getPendingOffers(): { student: Student | undefined; job: Job | undefined; app: Application }[] {
    return this.applicationsData
      .filter((a) => a.status === 'Selected' && a.offerStatus === 'Pending')
      .map((app) => ({
        app,
        student: this.studentsData.find((s) => s.id === app.studentId),
        job: this.jobsData.find((j) => j.id === app.jobId)
      }));
  }

  public getNewCompaniesThisWeek(): Job[] {
    const today = new Date('2026-08-11');
    const oneWeekAgo = new Date(today);
    oneWeekAgo.setDate(today.getDate() - 7);
    return this.jobsData.filter((j) => j.postedDate && new Date(j.postedDate) >= oneWeekAgo);
  }

  public getDynamicAttentionItems(): AttentionItem[] {
    const incCount = this.getIncompleteProfiles().length;
    const offCount = this.getPendingOffers().length;
    const compCount = this.getNewCompaniesThisWeek().length;

    return [
      {
        label: 'Profiles incomplete',
        value: incCount,
        iconType: 'user',
        tone: 'red',
        category: 'Profiles Incomplete',
        page: 'Students',
        filter: 'Profiles Incomplete'
      },
      {
        label: 'Offers awaiting response',
        value: offCount,
        iconType: 'file',
        tone: 'purple',
        category: 'Offers Awaiting Response',
        page: 'Students',
        filter: 'In Process'
      },
      {
        label: 'New companies this week',
        value: compCount,
        iconType: 'building',
        tone: 'green',
        category: 'New Companies This Week',
        page: 'Companies & Jobs',
        filter: 'New'
      }
    ];
  }

  // Overview KPIs
  public getOverviewKpis(): KpiOverview[] {
    const uniqueApplied = new Set(this.applicationsData.map((a) => a.studentId)).size;
    const assessmentsCleared = this.studentsData.filter((s) => s.assessmentsCleared > 0).length;
    const interviewsCleared = this.studentsData.filter((s) => s.interviewsCleared > 0).length;

    return [
      { label: 'Total Students', value: '2,438', note: '100% registered', iconType: 'users', tone: 'lilac' },
      { label: 'Eligible Students', value: '2,146', note: '88% of total students', iconType: 'check', tone: 'mint' },
      { label: 'Students Applied', value: uniqueApplied.toString(), note: '74% of eligible students', iconType: 'briefcase', tone: 'blue' },
      { label: 'Assessments Cleared', value: assessmentsCleared.toString(), note: '66% of eligible students', iconType: 'clipboard', tone: 'peach' },
      { label: 'Interviews Cleared', value: interviewsCleared.toString(), note: '40% of eligible students', iconType: 'calendar', tone: 'sky' },
      { label: 'Active Companies', value: this.jobsData.length.toString(), note: 'Based on current data', iconType: 'building', tone: 'green' }
    ];
  }

  public getDepartmentRates(): DepartmentPlacementRate[] {
    return [...this.departmentsList];
  }

  public getRecentActivities(): ActivityItem[] {
    return [...this.recentActivities];
  }

  // Placement Readiness
  public getReadinessKpis(): ReadinessKpis {
    const assessed = this.studentsData.filter((s) => s.assessmentsTaken > 0 || s.interviewsTaken > 0 || s.score > 0);
    const count = assessed.length || 1;
    const avg = Math.round(assessed.reduce((acc, s) => acc + s.score, 0) / count);
    const ready = assessed.filter((s) => s.score >= 80).length;
    const developing = assessed.filter((s) => s.score >= 60 && s.score < 80).length;
    const atRisk = assessed.filter((s) => s.score < 60).length;

    return {
      assessed: assessed.length,
      averageScore: avg,
      ready,
      developing,
      atRisk
    };
  }

  public getCohortData(): CohortBandItem[] {
    const kpis = this.getReadinessKpis();
    const safeTotal = kpis.assessed || 1;
    const pReady = Math.round((kpis.ready / safeTotal) * 100);
    const pDev = Math.round((kpis.developing / safeTotal) * 100);
    const pRisk = Math.max(0, 100 - pReady - pDev);

    return [
      { label: 'Ready', band: 'ready' as Band, count: kpis.ready, percent: pReady },
      { label: 'Developing', band: 'developing' as Band, count: kpis.developing, percent: pDev },
      { label: 'At Risk', band: 'at-risk' as Band, count: kpis.atRisk, percent: pRisk }
    ];
  }

  public getAiInsights(): InsightGroup[] {
    const mockInsights = [
      { insightId: 'INS-1', category: 'Assessment Gaps', priority: 'High', studentId: 'STU-2377', insight: 'Assessment clearance rate is 62.5%.', recommendedAction: 'Prioritize technical assessment practice.' },
      { insightId: 'INS-2', category: 'Assessment Gaps', priority: 'High', studentId: 'STU-2359', insight: 'Failed 3 technical assessments recently.', recommendedAction: 'Assign foundational coding practice modules.' },
      { insightId: 'INS-3', category: 'Interview Gaps', priority: 'High', studentId: 'STU-2377', insight: 'Clears assessments but 0/2 interviews cleared.', recommendedAction: 'Assign mock interviews with HR focus.' },
      { insightId: 'INS-4', category: 'Application Gaps', priority: 'Medium', studentId: 'STU-2364', insight: '0 active applications despite being eligible for 12.', recommendedAction: 'Increase applications to relevant open roles.' },
      { insightId: 'INS-5', category: 'Profile / Resume Gaps', priority: 'Medium', studentId: 'STU-2388', insight: 'Missing fields: Resume Upload, GitHub Link.', recommendedAction: 'Mandate profile completion.' },
      { insightId: 'INS-6', category: 'Profile / Resume Gaps', priority: 'High', studentId: 'STU-2330', insight: 'Missing fields: Portfolio Link.', recommendedAction: 'Prompt student to upload portfolio.' },
      { insightId: 'INS-7', category: 'Skill Gaps', priority: 'High', studentId: 'STU-2305', insight: 'Failed 3 consecutive coding challenges.', recommendedAction: 'Enroll in DSA remedial bootcamp.' },
      { insightId: 'INS-8', category: 'At Risk', priority: 'Critical', studentId: 'STU-2305', insight: 'Lowest readiness score, 22 credits used with minimal results.', recommendedAction: 'Schedule an immediate 1-on-1 counseling session.' },
      { insightId: 'INS-9', category: 'Placement Ready', priority: 'Low', studentId: 'STU-2401', insight: 'Cleared 3/4 assessments, 1/2 interviews.', recommendedAction: 'Fast-track to premium job opportunities.' },
      { insightId: 'INS-10', category: 'High Potential / Low Activity', priority: 'Medium', studentId: 'STU-2364', insight: '12 assessments cleared, 5 interviews cleared, 0 active apps.', recommendedAction: 'Check in on off-campus offers.' }
    ];

    const categories = [
      'Assessment Gaps',
      'Interview Gaps',
      'Application Gaps',
      'Profile / Resume Gaps',
      'Skill Gaps',
      'At Risk',
      'Placement Ready',
      'High Potential / Low Activity'
    ];

    return categories
      .map((cat) => {
        const catInsights = mockInsights.filter((i) => i.category === cat);
        if (catInsights.length === 0) return null;

        let p: Priority = 'Medium Priority';
        if (catInsights[0].priority === 'Critical') p = 'Critical';
        else if (catInsights[0].priority === 'High') p = 'High Priority';
        else if (catInsights[0].priority === 'Medium') p = 'Medium Priority';
        else if (catInsights[0].priority === 'Low') p = 'Low Priority';

        return {
          id: cat.toLowerCase().replace(/ \/ /g, '-').replace(/ /g, '-'),
          name: cat,
          students: catInsights.length,
          priority: p,
          explanation: catInsights[0].insight
        };
      })
      .filter((g): g is InsightGroup => g !== null);
  }

  // Notifications
  public getNotifications(): NotificationItem[] {
    return this.notificationsSubject.value;
  }

  public getUnreadNotificationCount(): number {
    return this.notificationsSubject.value.filter((n) => n.unread).length;
  }

  public markNotificationAsRead(id: number): void {
    const updated = this.notificationsSubject.value.map((n) =>
      n.id === id ? { ...n, unread: false } : n
    );
    this.notificationsSubject.next(updated);
  }

  public markAllNotificationsAsRead(): void {
    const updated = this.notificationsSubject.value.map((n) => ({ ...n, unread: false }));
    this.notificationsSubject.next(updated);
    this.notify('All notifications marked as read');
  }
}
